import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { Button, Callout, useOverlayContext, useStateInRef } from 'seagull';
import { useStore, useStoreActions } from '~/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { handlePromise } from '~/infra/utils';
import { getAccountingDetails } from '~/integrations/Accounting/accounting.utils';
import { accountingIconMap } from '~/integrations/Accounting/AccountingIcon/accountingIconMap';
import { useOpenMapContactsModal } from '~/integrations/Accounting/MapContactsModal/useOpenMapContactsModal';
import { useOpenMapServicesModal } from '~/integrations/Accounting/MapServicesModal/useOpenMapServicesModal';
import { invoiceModel } from '~/models';
import { selectors } from '~/store/state-selectors';
import { getAccountingSectionCallout, getServiceTemplateIdsV1, utilsTextsPath } from './FullPageInvoice.utils';
const syncLoadingText = 'Syncing...';
var SyncingState = /*#__PURE__*/ function(SyncingState) {
    SyncingState["Syncing"] = "syncing";
    SyncingState["SyncingRetry"] = "syncingRetry";
    SyncingState["SyncingRetryWithoutTimeActivities"] = "syncingRetryWithoutTimeActivities";
    return SyncingState;
}(SyncingState || {});
export default function FullPageInvoiceAccountingCallout(props) {
    const { invoice, contacts, serviceTemplates, relationship, reloadInvoice, accounting, integration } = props;
    const { showErrorToast, showSuccessToast } = useOverlayContext();
    const { t: fullPageTexts } = useLocaleKeys('invoice.fullPage');
    const { api } = useStoreActions();
    const { SERVICE_TEMPLATE_BUNDLE } = useFeatureFlags();
    const invoiceClientContactId = useMemo(()=>{
        const invoiceContact = contacts.find((c)=>c.id() === relationship.clientContactId);
        return invoiceContact ? invoiceContact.id() : null;
    }, [
        relationship.clientContactId,
        contacts
    ]);
    const serviceTemplateIds = SERVICE_TEMPLATE_BUNDLE ? getAccountingDetails(accounting).policy.getServiceTemplateIds(invoice) : getServiceTemplateIdsV1(invoice);
    const openMapServicesModal = useOpenMapServicesModal(serviceTemplateIds, reloadInvoice);
    const openMapContactsModal = useOpenMapContactsModal(invoiceClientContactId, reloadInvoice);
    const [isSyncing, setIsSyncing] = useState(null);
    const [isSyncingPayment, setIsSyncingPayment] = useState(false);
    const [isSyncingRefund, setIsSyncingRefund] = useState(false);
    const isLinkedToAccounting = useStore(selectors.integration.accounting(accounting).isLinked);
    const featureFlags = useFeatureFlags();
    const { t: acctTexts } = useLocaleKeys('components.fullPageInvoice.accountingCallout');
    const { t: utilsTexts } = useLocaleKeys(utilsTextsPath);
    const getState = useStateInRef({
        isSyncing,
        isSyncingPayment,
        isSyncingRefund,
        onChange: props.onChange,
        showSyncSuccessToast: (formattedNumber)=>showSuccessToast(fullPageTexts.invoiceSyncedSuccess({
                invoiceNumber: formattedNumber
            }), 'sync-success-toast'),
        showSyncErrorToast: (formattedNumber)=>showErrorToast(fullPageTexts.failedToSyncInvoice({
                invoiceNumber: formattedNumber
            }), 'sync-failure-toast'),
        showSyncPaymentSuccessToast: ()=>showSuccessToast(fullPageTexts.paymentSyncedSuccess(), 'payment-sync-success'),
        showSyncPaymentFailureToast: ()=>showErrorToast(fullPageTexts.failedToSyncPayment(), 'payment-sync-failure'),
        showSyncRefundSuccessToast: ()=>showSuccessToast(fullPageTexts.refundSyncedSuccess(), 'refund-sync-success'),
        showSyncRefundFailureToast: ()=>showErrorToast(fullPageTexts.failedToSyncRefund(), 'refund-sync-failure')
    });
    useEffect(()=>{
        if (!integration) return;
        const { isSyncing, isSyncingPayment, isSyncingRefund, onChange, showSyncSuccessToast, showSyncErrorToast, showSyncPaymentFailureToast, showSyncPaymentSuccessToast, showSyncRefundSuccessToast, showSyncRefundFailureToast } = getState();
        if (isSyncing) {
            setIsSyncing(null);
            onChange(integration);
            if (integration.isInvoiceSyncFailed()) {
                showSyncErrorToast(invoice.formattedNumber);
            } else if (integration.isInvoiceRecorded() || integration.isPaymentRecorded()) {
                showSyncSuccessToast(invoice.formattedNumber);
            }
        }
        if (isSyncingPayment) {
            setIsSyncingPayment(false);
            onChange(integration);
            if (integration.isPaymentSyncFailed()) {
                showSyncPaymentFailureToast();
            } else if (integration.isPaymentRecorded()) {
                showSyncPaymentSuccessToast();
            }
        }
        if (isSyncingRefund) {
            setIsSyncingRefund(false);
            onChange(integration);
            if (integration.isRefundSyncFailed()) {
                showSyncRefundFailureToast();
            } else if (integration.isRefundRecorded()) {
                showSyncRefundSuccessToast();
            }
        }
    }, [
        integration,
        invoice
    ]);
    const callout = isLinkedToAccounting ? getAccountingSectionCallout({
        accounting,
        items: invoice.items,
        relationship,
        contacts,
        serviceTemplates,
        integration: integration,
        invoiceStatus: invoiceModel.statusType(invoice),
        numRefunds: invoiceModel.numRefunds(invoice),
        isZeroAmount: invoice.isTotalAmountZero,
        syncAction: {
            id: 'sync-invoice',
            text: acctTexts.sync(),
            loading: isSyncing === "syncing",
            loadingText: syncLoadingText,
            onClick: ()=>syncInvoice({
                    syncState: "syncing"
                })
        },
        retrySyncAction: {
            id: 'retry-sync-invoice',
            text: acctTexts.retrySync(),
            loading: isSyncing === "syncingRetry",
            disabled: !!isSyncing && isSyncing !== "syncingRetry",
            loadingText: syncLoadingText,
            onClick: ()=>syncInvoice({
                    isRetry: true,
                    syncState: "syncingRetry"
                })
        },
        retrySyncWithoutTimeActivitiesAction: {
            id: 'retry-sync-without-time-activities',
            text: acctTexts.syncWithoutTimeActivities(),
            loading: isSyncing === "syncingRetryWithoutTimeActivities",
            disabled: !!isSyncing && isSyncing !== "syncingRetryWithoutTimeActivities",
            loadingText: syncLoadingText,
            onClick: ()=>syncInvoice({
                    isRetry: true,
                    includeTimeActivities: false,
                    syncState: "syncingRetryWithoutTimeActivities"
                })
        },
        mapContactsAction: {
            id: 'map-contact',
            text: acctTexts.mapContact(),
            onClick: ()=>openMapContactsModal(accounting)
        },
        mapServicesAction: {
            id: 'map-services',
            text: acctTexts.mapServices(),
            intent: Intent.AccountingMapServices,
            onClick: ()=>openMapServicesModal(accounting)
        },
        syncPaymentAction: {
            id: 'sync-payment',
            text: acctTexts.sync(),
            loading: isSyncingPayment,
            loadingText: syncLoadingText,
            onClick: ()=>triggerSync({
                    sendRequest: ()=>api.accounting.syncPayment({
                            accounting,
                            invoiceId: invoice.id
                        }),
                    onLoading: ()=>setIsSyncingPayment(true),
                    onError: ()=>{
                        getState().showSyncPaymentFailureToast();
                        setIsSyncingPayment(false);
                    }
                })
        },
        syncRefundAction: {
            id: 'sync-refund',
            text: acctTexts.sync(),
            loading: isSyncingRefund,
            loadingText: syncLoadingText,
            onClick: ()=>triggerSync({
                    sendRequest: ()=>api.accounting.syncRefund(accounting, invoice.id),
                    onLoading: ()=>setIsSyncingRefund(true),
                    onError: ()=>{
                        getState().showSyncRefundFailureToast();
                        setIsSyncingRefund(false);
                    }
                })
        },
        featureFlags
    }, utilsTexts) : null;
    async function syncInvoice(param) {
        let { isRetry, syncState, includeTimeActivities } = param;
        triggerSync({
            sendRequest: ()=>{
                return isRetry ? api.accounting.retrySyncInvoice(accounting, invoice.id, includeTimeActivities) : api.accounting.syncInvoice(accounting, invoice.id);
            },
            onLoading: ()=>setIsSyncing(syncState),
            onError: ()=>{
                getState().showSyncErrorToast(invoice.formattedNumber);
                setIsSyncing(null);
            }
        });
    }
    async function triggerSync(param) {
        let { sendRequest, onLoading, onError } = param;
        onLoading();
        const { success } = await handlePromise(sendRequest());
        if (!success) {
            onError();
        }
    }
    return callout ? /*#__PURE__*/ _jsx(Callout, {
        intent: callout.intent,
        icon: accountingIconMap[accounting][callout.status],
        testId: "fpi-".concat(callout.id),
        withAction: !!callout.actions.length && /*#__PURE__*/ _jsx(_Fragment, {
            children: callout.actions.map((_0)=>{
                let { id, text } = _0, rest = _object_without_properties(_0, [
                    "id",
                    "text"
                ]);
                return /*#__PURE__*/ _jsx(Button, _object_spread_props(_object_spread({
                    testId: id,
                    skin: "primaryOutline"
                }, rest), {
                    children: text
                }), id);
            })
        }),
        children: callout.text
    }) : null;
}
